import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
// logic
import { useTranslation } from 'react-i18next';
// components
import ActionsMenuItem from './ActionsMenuItem';
import {
  ManifestIcon,
  RepeatIcon,
  EditIcon,
  CancelIcon,
  CopyIcon,
  TrackingIcon,
  PrintIcon,
} from 'refactored/ui/icons';
import useActionsMenu from './useActionsMenu';
import { IS_TENANT } from 'tenant/utils';

const ActionsMenu = ({
  className,
  onClickOutside,
  handleOptionClick,
  cargoManifests,
  numberOfStops,
  order,
  onUrlCopied,
  type,
}) => {
  const { t } = useTranslation('ShipmentCard');
  const ref = useRef(null);

  const onClick = useCallback(
    (e) => {
      if (!ref?.current || ref.current.contains(e.target)) {
        return;
      }
      onClickOutside(e);
    },
    [onClickOutside]
  );

  useEffect(() => {
    document.addEventListener('click', onClick, false);
    return () => {
      document.removeEventListener('click', onClick, false);
    };
  }, [onClick]);

  const {
    isCancellable,
    cancelOrder,
    isRepeatable,
    repeatOrder,
    isEditable,
    editOrder,
    isDownloadable,
    downloadManifest,
    showShareActions,
    openTrackingPage,
    copyTrackingPageLink,
    printOrderDetails,
    isControlDocumentDownloadable,
    downloadControlDocument,
  } = useActionsMenu({
    handleOptionClick,
    cargoManifests,
    numberOfStops,
    order,
    onUrlCopied,
    type,
  });

  return (
    <ul className={className} ref={ref}>
      {!IS_TENANT && (
        <>
          <ActionsMenuItem
            data-tid="edit-btn"
            onClick={editOrder}
            disabled={!isEditable}
            icon={<EditIcon />}
          >
            {t('Orders:edit.action')}
          </ActionsMenuItem>
          <ActionsMenuItem
            data-tid="repeat-btn"
            onClick={repeatOrder}
            disabled={!isRepeatable}
            icon={<RepeatIcon />}
          >
            {t('menu.repeat')}
          </ActionsMenuItem>
          <ActionsMenuItem
            data-tid="cancel-btn"
            onClick={cancelOrder}
            danger
            disabled={!isCancellable}
            icon={<CancelIcon />}
          >
            {t('Orders:cancel.action')}
          </ActionsMenuItem>{' '}
        </>
      )}
      {showShareActions && (
        <>
          <ActionsMenuItem
            onClick={openTrackingPage}
            disabled={!isRepeatable}
            icon={<TrackingIcon />}
          >
            {t('menu.open_public_tracking_page')}
          </ActionsMenuItem>
          <ActionsMenuItem
            onClick={copyTrackingPageLink}
            disabled={!isRepeatable}
            icon={<CopyIcon />}
          >
            {t('menu.copy_public_tracking_page_link')}
          </ActionsMenuItem>
        </>
      )}
      <ActionsMenuItem onClick={printOrderDetails} icon={<PrintIcon />}>
        {t('ModalOrderDetail:footer.print_button')}
      </ActionsMenuItem>
      <ActionsMenuItem
        onClick={downloadManifest}
        disabled={!isDownloadable}
        icon={<ManifestIcon />}
      >
        {isDownloadable
          ? t('menu.download_cargo_manifests')
          : t('menu.no_cargo_manifests')}
      </ActionsMenuItem>
      {isControlDocumentDownloadable && (
        <ActionsMenuItem
          data-tid="download-deca-btn"
          onClick={downloadControlDocument}
          icon={<ManifestIcon />}
        >
          {t('menu.download_deca')}
        </ActionsMenuItem>
      )}
    </ul>
  );
};

ActionsMenu.propTypes = {
  className: PropTypes.string,
  cargoManifests: PropTypes.arrayOf(PropTypes.string),
  numberOfStops: PropTypes.number,
  order: PropTypes.shape({
    journey_type: PropTypes.string,
  }),
  handleOptionClick: PropTypes.func,
};

ActionsMenu.defaultProps = {
  cargoManifests: [],
  numberOfStops: 0,
  order: {},
};

export default ActionsMenu;
